import React, { useEffect, useState } from "react";
import api from "../../lib/api";
import { Link } from "react-router-dom";
import { StatTile, PageHeader } from "../../components/ui-extras";
import { useAuth } from "../../lib/auth";
import MixedCountryCharts from "../../components/MixedCountryCharts";
import CustomerDashboardCharts from "../../components/CustomerDashboardCharts";
import { formatCard } from "../../lib/cardDisplay";

/**
 * Customer dashboard.
 *
 * Invoice widgets and the "AMOUNT DUE" tile are intentionally hidden
 * during the CP-branding pass. Backend invoice endpoints and admin
 * invoice UI remain fully intact — customer-facing invoicing will
 * return in a future phase.
 */
export default function CustomerDashboard() {
  const { user } = useAuth();
  const [stats, setStats] = useState(null);
  const [txs, setTxs] = useState([]);
  const [lanes, setLanes] = useState(null);

  useEffect(() => {
    api.get("/dashboard/stats").then(({ data }) => setStats(data));
    api.get("/transactions", { params: { limit: 10 } })
      .then(({ data }) => setTxs(data));
    // Fetch the authenticated customer's authorized country set so the
    // dashboard only offers panels the customer is entitled to see.
    // Server enforces scoping — customer_id comes from the auth token,
    // not from the browser (see backend/routes/customers.py).
    if (user?.customer_id) {
      api.get(`/customers/${user.customer_id}`)
        .then(({ data }) => setLanes(data?.authorized_lanes || []))
        .catch(() => setLanes([]));
    }
  }, [user?.customer_id]);

  const fmt$ = (n) =>
    `$${(n || 0).toLocaleString(undefined, { maximumFractionDigits: 2 })}`;

  return (
    <>
      <PageHeader
        title={user?.name ? `Hi, ${user.name.split(" ")[0]}` : user?.customer_name ? `Welcome, ${user.customer_name}` : "Welcome"}
        subtitle="PORTAL / OVERVIEW"
        testId="portal-dashboard-header"
      />

      {!user?.customer_id && (
        <div className="gf-card mb-6">
          <div className="gf-card-body">
            <div className="overline">ACCOUNT NOT LINKED</div>
            <div className="text-sm mt-2" style={{ color: "#4B5563" }}>
              Your email isn't linked to a customer account yet. Please
              contact hello@getfuelcard.com to get linked.
            </div>
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
        <StatTile
          label="LITRES (30D)"
          value={`${(stats?.volume_30d || 0).toFixed(0)} L`}
          sub="total fuel across your fleet"
          testId="portal-stat-vol"
        />
        <StatTile
          label="SPEND (30D)"
          value={fmt$(stats?.revenue_30d)}
          sub="across your fleet"
          testId="portal-stat-spend"
        />
      </div>

      {/* Phase 3E-9N — Geographic analytics for the authenticated customer.
          MixedCountryCharts renders a Province panel for CA-only customers,
          a State panel for US-only customers, or both panels for customers
          authorized on both countries. All data flows through the existing
          per-customer /fueling-by-city endpoint which is server-side scoped
          to req.user.customer_id — no cross-customer leakage possible. */}
      {user?.customer_id && lanes !== null && (() => {
        const cc = new Set((lanes || []).map((l) => (l.country || "").toUpperCase()));
        const country = cc.size === 1 ? [...cc][0] : undefined;
        return (
          <MixedCountryCharts
            customerId={user.customer_id}
            country={country}
            defaultDays={90}
          />
        );
      })()}

      <div className="grid grid-cols-1 gap-4">
        <div className="gf-card">
          <div className="gf-card-header flex items-center justify-between">
            <h3>Latest fuelings</h3>
            <Link to="/portal/transactions" className="overline no-brand" style={{ color: "#6B7280" }}>
              ALL →
            </Link>
          </div>
          <table className="gf-table">
            <thead>
              <tr>
                <th>DATE</th>
                <th>CARD</th>
                <th>STATION</th>
                <th className="text-right">QTY</th>
                <th className="text-right">AMT</th>
              </tr>
            </thead>
            <tbody>
              {txs.slice(0, 8).map((t) => (
                <tr key={t.transaction_id}>
                  <td className="font-mono text-xs">{t.tran_date}</td>
                  <td className="font-mono">{formatCard(t.card_number, t.network, t.card_last4)}</td>
                  <td className="text-xs">{t.station_name}</td>
                  <td className="gf-num">{t.qty?.toFixed(1)}</td>
                  <td className="gf-num">${t.customer_amount?.toFixed(2)}</td>
                </tr>
              ))}
              {txs.length === 0 && (
                <tr>
                  <td colSpan={5} className="text-center py-6" style={{ color: "#9CA3AF" }}>
                    No fueling activity yet.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {user?.customer_id && <CustomerDashboardCharts />}
    </>
  );
}
